/**
 * The display shell. Joins with the `?t=` display token (stored per code) or, with none,
 * the host's cookie. The join's own refusal is the gate.
 */

import { useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useSession } from '../lib/useSession';
import { uiFor } from '../games';
import { loadDisplayToken, saveDisplayToken } from '../lib/storage';
import { Toast } from '../kit/Toast';
import { Interstitial } from '../show/Interstitial';
import { StartCountdown } from '../show/StartCountdown';

export function Board() {
  const { code = '' } = useParams();
  const [params] = useSearchParams();
  const fromLink = params.get('t') ?? '';

  // Persist so a reload doesn't need the link again.
  useEffect(() => {
    if (code && fromLink) saveDisplayToken(code, fromLink);
  }, [code, fromLink]);

  const token = fromLink || loadDisplayToken(code);
  return <BoardInner code={code} displayToken={token} />;
}

function BoardInner({ code, displayToken }: { code: string; displayToken: string }) {
  const autoJoin = useMemo(
    // Undefined, not '': the server reads absent as "try the cookie" and wrong as a refusal.
    () => ({ as: 'display' as const, code, displayToken: displayToken || undefined }),
    [code, displayToken],
  );
  const { snapshot, connected, joinError, events, toast, clearToast, send, submit, notify } =
    useSession(autoJoin);

  if (joinError) {
    return (
      <div className="centered">
        <div className="card col">
          <h1>Can’t show “{code}”</h1>
          <p className="error-text">{joinError}</p>
          <p className="muted" style={{ margin: 0 }}>
            Ask the host for the TV link — it is on their Host Control screen.
          </p>
          <Link className="btn" to="/">
            Back
          </Link>
        </div>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="centered">
        <p className="muted">{connected ? 'Loading game…' : 'Connecting…'}</p>
      </div>
    );
  }

  if (!snapshot.core.segment) {
    return (
      <>
        <Interstitial snapshot={snapshot} send={send} surface="display" connected={connected} />
        <Toast message={toast} onDismiss={clearToast} />
      </>
    );
  }

  const ui = uiFor(snapshot.core.segment.gameId);

  return (
    <>
      {ui ? (
        <ui.display
          snapshot={snapshot}
          send={send}
          submit={submit}
          events={events}
          connected={connected}
          notify={notify}
        />
      ) : (
        <div className="centered">
          <h1>{snapshot.core.title}</h1>
        </div>
      )}
      <StartCountdown snapshot={snapshot} />
      <Toast message={toast} onDismiss={clearToast} />
    </>
  );
}
